import React, { ComponentProps } from "react";
import { MdOutlineLuggage } from "react-icons/md";
import { IoBrushSharp, IoChatbubblesOutline } from "react-icons/io5";
import { Button } from "../../atoms/Button/Button";
import { Step } from "./Components";
import { Separator } from "./Components/Separator";
import { Icomoon } from "../../atoms/Icon/Icon";
import "./HowItWorks.scss";
import { useTranslation } from "react-i18next";
import { TrustLink } from "../../molecules/TrustLink/TrustLink";
import { Locale } from "@travellocal/utils";
import { useAppState } from "../../utils";
import { Trustpilot } from "../../atoms";

/**
 * HowItWorks Component
 *
 * A component that displays a 3-step process explaining how the TravelLocal service works.
 * It includes steps with icons, descriptions, and an optional CTA button.
 *
 * @component
 * @example
 * ```tsx
 * <HowItWorks
 *   ctaUrl="/start"
 *   dataTrackingId="homepage-how-it-works"
 *   locale="en-gb"
 * />
 * ```
 */
interface HowItWorks {
  /**
   * Optional URL for the call-to-action button. If not provided, the button will not be displayed.
   */
  ctaUrl?: string;
  /**
   * ID used for tracking user interactions with the component.
   */
  dataTrackingId: string;
  /**
   * The locale code to use for translations and regional content.
   */
  locale: Locale;
  /**
   * Whether to show the Trustpilot component between steps and CTA button.
   */
  trustpilotProps?: Omit<ComponentProps<typeof Trustpilot>, "template">;
}

export const HowItWorks = ({ ctaUrl, dataTrackingId, locale, trustpilotProps }: HowItWorks) => {
  const { t } = useTranslation("ui");
  const { appState } = useAppState();
  const currentLocale = locale || appState?.locale || "en-gb";

  return (
    <div className="step-by-step-wrapper">
      <h3>{t("HowItWorks_H3", "How it works")}</h3>
      <div className="steps">
        <Step
          className="step"
          stepNumber={1}
          Icon={<IoBrushSharp size="20px" />}
          description={t(
            "HowItWorks_Step1_Description",
            "Let us know about your trip and we'll find you the perfect local agent."
          )}
          title={t("HowItWorks_Step1_Title", "Tell us your preferences")}
        />
        <Separator />
        <Step
          className="step"
          stepNumber={2}
          Icon={<IoChatbubblesOutline size="20px" />}
          description={t(
            "HowItWorks_Step2_Description",
            "Collaborate with them to create your perfect itinerary."
          )}
          title={t("HowItWorks_Step2_Title", "Plan with your local agent")}
        />
        <Separator />
        <Step
          className="step"
          stepNumber={3}
          Icon={<MdOutlineLuggage size="20px" />}
          description={
            <>
              {t("HowItWorks_Step3_Description", "")} <TrustLink locale={currentLocale} />
              {"."}
            </>
          }
          title={t("HowItWorks_Step3_Title", "Book your trip!")}
        />
      </div>

      {trustpilotProps && (
        <div className="trustpilot-container">
          <Separator className="mt-5 mb-6" />
          <Trustpilot {...trustpilotProps} template="carousel" theme="dark" />
        </div>
      )}

      {ctaUrl && (
        <Button
          colour="teal"
          icomoonIcon={Icomoon.arrowRight3}
          href={ctaUrl}
          target="_self"
          data-tracking-id={dataTrackingId}
          className="cta-button">
          {t("HowItWorks_CTA", "Get started")}
        </Button>
      )}
    </div>
  );
};
